const STORAGE_KEY = 'jc.savedSearches.deletedTombstones';

/**
 * Bridges the gap between an optimistic local delete and the DELETE request
 * actually landing server-side: long enough to outlive a slow request or one
 * cut short by an immediate page refresh, short enough not to block someone
 * from re-saving the same search later.
 */
const TOMBSTONE_TTL_MS = 5 * 60 * 1000;

function readTombstones(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeTombstones(tombstones: Record<string, number>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tombstones));
  } catch {
    // Best-effort persistence; a lost tombstone just means a possible resurrection, not a crash.
  }
}

function pruneExpired(tombstones: Record<string, number>, now: number): Record<string, number> {
  const next: Record<string, number> = {};
  for (const [id, deletedAt] of Object.entries(tombstones)) {
    if (typeof deletedAt === 'number' && now - deletedAt < TOMBSTONE_TTL_MS) next[id] = deletedAt;
  }
  return next;
}

/** Record that the user deleted a saved search item, keyed by its server id. */
export function recordSavedSearchDeleted(id: string): void {
  const now = Date.now();
  const tombstones = pruneExpired(readTombstones(), now);
  tombstones[id] = now;
  writeTombstones(tombstones);
}

/** Clear a tombstone once the delete is confirmed to have landed server-side. */
export function clearSavedSearchDeleted(id: string): void {
  const tombstones = readTombstones();
  if (!(id in tombstones)) return;
  delete tombstones[id];
  writeTombstones(tombstones);
}

/** Whether this id was deleted locally recently and shouldn't be pulled back from the server. */
export function isRecentlySavedSearchDeleted(id: string): boolean {
  const tombstones = pruneExpired(readTombstones(), Date.now());
  return id in tombstones;
}
